// src/components/QuoteCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useTranslation } from "react-i18next";
import { rebrandString } from "../lib/rebrand";

export default function QuoteCard({
  pageKey = "collector.dashboard", // ✅ default stable
  topics = null,                  // can be array of strings
  lang: langProp = null,
  context = "dashboard",          // ✅ configurable from parent
  cooldownHours = 72,             // ✅ configurable
  maxRecent = 10,                 // ✅ configurable
  variant = null,                 // ✅ force un style (ex. "postit") quel que soit quote.style
  debug = false,
}) {
  const { i18n } = useTranslation();
  const lang = langProp || String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ normalize topics safely into text[]
  const topicsArray = useMemo(() => {
    if (!topics) return null;
    if (Array.isArray(topics)) return topics.filter(Boolean).map(String);
    // if someone passes a single string
    if (typeof topics === "string") return [topics];
    return null;
  }, [topics]);

  useEffect(() => {
    let mounted = true;

async function loadQuote() {
  try {
    setLoading(true);

    const payload = {
      page_key: pageKey ?? "collector.dashboard",
      p_topics: topicsArray,
      p_lang: lang,
      cooldown_hours: cooldownHours,
      max_recent: maxRecent,
      p_context: context,
    };

    if (debug) {
      console.log("[QuoteCard] RPC:", "get_random_quote_for_user_compat");
      console.log("[QuoteCard] payload:", payload);
    }

    const { data, error } = await supabase.rpc("get_random_quote_for_user_compat", payload);

    if (!mounted) return;

    if (debug) {
      console.log("[QuoteCard] raw data:", data);
      console.log("[QuoteCard] raw error:", error);
      console.log("[QuoteCard] data type:", Array.isArray(data) ? "array" : typeof data);
      if (Array.isArray(data)) console.log("[QuoteCard] array length:", data.length);
    }

    if (error) {
      console.error("[QuoteCard] RPC error:", error);
      setQuote(null);
      setLoading(false);
      return;
    }

    const q = Array.isArray(data) ? data[0] : data;

    if (debug) {
      console.log("[QuoteCard] picked q:", q);
      console.log("[QuoteCard] q keys:", q ? Object.keys(q) : null);
    }

    // ✅ body fallback: compat + legacy
    const body =
      q?.text ??
      q?.quote_text ??
      q?.content ??
      q?.message ??
      "";

    if (!String(body).trim()) {
      if (debug) console.log("[QuoteCard] EMPTY: no quote returned or text empty", { q, body });
      setQuote(null);
      setLoading(false);
      return;
    }

    // ✅ normalise q.text so rendering is stable. Les citations viennent de la base (hors
    // i18n) : on leur applique la marque du build (ex. « ArtKiv » → « Artichaut » sur ARTICHAUT).
    const normalized = {
      ...q,
      text: rebrandString(String(body)),
      author: q.author ? rebrandString(String(q.author)) : q.author,
    };

    setQuote(normalized);
    setLoading(false);
  } catch (err) {
    console.error("[QuoteCard] Exception:", err);
    if (mounted) {
      setQuote(null);
      setLoading(false);
    }
  }
}
    loadQuote();
    return () => {
      mounted = false;
    };
  }, [pageKey, topicsArray, lang, context, cooldownHours, maxRecent, debug]);

  if (loading || !quote) return null;

  const body = quote.text ?? "";
  if (!String(body).trim()) return null;

  // 🎨 Styles élégants façon Artsy
  const styles = {
    postit: {
      container: {
        background: "linear-gradient(135deg, #FFF9E5 0%, #F6F3E8 100%)",
        borderRadius: 8,
        padding: "20px 24px",
        maxWidth: 320,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.08)",
        border: "1px solid rgba(201,169,97,0.15)",
        position: "relative",
        marginTop: 24,
      },
      text: {
        fontFamily: "Georgia, serif",
        fontSize: "1rem",
        lineHeight: 1.6,
        color: "#1a1a1a",
        fontStyle: "italic",
        marginBottom: 12,
      },
      author: {
        fontFamily: "Inter, sans-serif",
        fontSize: "0.85rem",
        color: "#c9a961",
        fontStyle: "normal",
        fontWeight: 500,
      },
    },
    notebook: {
      container: {
        borderLeft: "3px solid #c9a961",
        paddingLeft: 24,
        marginTop: 32,
        maxWidth: 680,
        background: "rgba(249,247,244,0.3)",
        paddingTop: 16,
        paddingBottom: 16,
        paddingRight: 16,
      },
      text: {
        fontFamily: "Georgia, serif",
        fontSize: "1.05rem",
        lineHeight: 1.8,
        color: "#1a1a1a",
        fontStyle: "italic",
        marginBottom: 12,
      },
      author: {
        fontFamily: "Inter, sans-serif",
        fontSize: "0.9rem",
        color: "rgba(0,0,0,0.6)",
        fontStyle: "normal",
        fontWeight: 500,
      },
    },
    annotation: {
      container: {
        marginTop: 16,
        maxWidth: 560,
        padding: "12px 0",
      },
      text: {
        fontFamily: "Inter, sans-serif",
        fontSize: "0.875rem",
        lineHeight: 1.6,
        color: "rgba(0,0,0,0.55)",
        fontStyle: "italic",
        marginBottom: 8,
      },
      author: {
        fontFamily: "Inter, sans-serif",
        fontSize: "0.75rem",
        color: "rgba(0,0,0,0.45)",
        fontStyle: "normal",
        fontWeight: 500,
      },
    },
  };

  // ✅ robust style fallback — `variant` (prop) prime sur quote.style
  const styleKey =
    variant && styles[variant]
      ? variant
      : styles[String(quote?.style || "")]
      ? String(quote.style)
      : "postit";

  // Post-it « moodboard » éditorial : papier crème, washi tape, guillemet, filet or.
  if (styleKey === "postit") {
    return (
      <figure
        style={{
          marginTop: 24,
          maxWidth: 340,
          position: "relative",
          transform: "rotate(-1.2deg)",
        }}
      >
        {/* washi tape */}
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: -9,
            left: "50%",
            transform: "translateX(-50%) rotate(-3deg)",
            width: 88,
            height: 20,
            background:
              "linear-gradient(180deg, rgba(201,169,97,0.38), rgba(201,169,97,0.16))",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          }}
        />
        <div
          style={{
            background: "linear-gradient(135deg,#fffdf5 0%,#f7f2e6 100%)",
            borderRadius: 10,
            padding: "26px 26px 22px",
            border: "1px solid rgba(201,169,97,0.22)",
            boxShadow:
              "0 2px 8px rgba(26,26,26,0.06), 0 14px 30px rgba(26,26,26,0.10)",
            position: "relative",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 6,
              left: 16,
              fontFamily: "Georgia, serif",
              fontSize: 56,
              lineHeight: 1,
              color: "rgba(201,169,97,0.30)",
            }}
          >
            &ldquo;
          </span>
          <blockquote
            style={{
              margin: 0,
              paddingTop: 14,
              fontFamily: "Georgia, serif",
              fontSize: "1.05rem",
              lineHeight: 1.65,
              color: "#1a1a1a",
              fontStyle: "italic",
              position: "relative",
            }}
          >
            {body}
          </blockquote>
          {quote.author && (
            <figcaption
              style={{
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span aria-hidden="true" style={{ width: 22, height: 1, background: "#c9a961" }} />
              <span
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.82rem",
                  letterSpacing: "0.02em",
                  color: "#8a6d2f",
                  fontWeight: 600,
                }}
              >
                {quote.author}
              </span>
            </figcaption>
          )}
        </div>
      </figure>
    );
  }

  return (
    <div style={styles[styleKey].container}>
      <div style={styles[styleKey].text}>{body}</div>
      {quote.author && <div style={styles[styleKey].author}>— {quote.author}</div>}
    </div>
  );
}
